import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { getMarketStatus } from '../utils/marketStatus';

const GAME_LOOKAHEAD_WINDOW_MS = 24 * 60 * 60 * 1000;
const GAME_BACKFILL_WINDOW_MS = 6 * 60 * 60 * 1000;

// Transform API response to our format
function transformMarket(m) {
  return {
    id: m.marketId,
    conditionId: m.conditionId,
    slug: m.marketSlug,
    title: m.title,
    teamA: m.teamA?.name || m.teamA,
    teamB: m.teamB?.name || m.teamB,
    priceA: m.prices?.yes || 0,
    priceB: m.prices?.no || 0,
    polyYes: m.prices?.yes,
    polyNo: m.prices?.no,
    volume: m.volume || 0,
    liquidity: m.liquidity || 0,
    status: getMarketStatus(m),
    gameTime: m.startTime,
    sport: m.sport,
    polymarketUrl: `https://polymarket.com/event/${m.eventSlug}`,
    recentFormA: m.recentFormA || m.recent_form_a || [],
    recentFormB: m.recentFormB || m.recent_form_b || [],
    // Pass through AI probabilities if available, fallback to Poly odds
    aiProbA: m.aiProbA ?? m.prices?.yes,
    aiProbB: m.aiProbB ?? m.prices?.no,
    pointsUnlocked: m.points_unlocked ?? m.pointsUnlocked ?? false,
    // Pass through postponed status from ESPN
    postponed: m.postponed ?? false,
  };
}

export function useMarkets(limit = 60) {
  const [markets, setMarkets] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMarkets = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getMarkets(limit);

      const payload = data?.data || data;
      const marketList = Array.isArray(payload) ? payload : (payload?.markets || []);
      const predictionPayload = payload?.predictions;
      const transformed = marketList.map(transformMarket);
      let nextMarkets = transformed;

      const liveCandidates = nextMarkets.filter(
        (m) => m.status === 'live' && m.teamA && m.teamB && m.gameTime
      );
      if (liveCandidates.length > 0) {
        const results = await Promise.all(
          liveCandidates.map((m) =>
            api.findGame(m.teamA, m.teamB, m.gameTime).catch(() => null)
          )
        );
        const liveIds = new Set(
          results
            .map((res, idx) => (res?.success && res?.gameId ? liveCandidates[idx].id : null))
            .filter(Boolean)
        );

        nextMarkets = nextMarkets.map((m) => {
          if (m.status !== 'live') return m;
          return liveIds.has(m.id) ? m : { ...m, status: 'upcoming' };
        });
      }

      const now = Date.now();
      const windowedCandidates = nextMarkets.filter((m) => {
        if (!m.teamA || !m.teamB || !m.gameTime) return false;
        const ts = new Date(m.gameTime).getTime();
        if (Number.isNaN(ts)) return false;
        return ts >= now - GAME_BACKFILL_WINDOW_MS && ts <= now + GAME_LOOKAHEAD_WINDOW_MS;
      });

      if (windowedCandidates.length > 0) {
        const results = await Promise.all(
          windowedCandidates.map((m) =>
            api.findGame(m.teamA, m.teamB, m.gameTime).catch(() => null)
          )
        );
        const hiddenIds = new Set(
          results
            .map((res, idx) =>
              res?.success === false || (res?.success === true && !res?.gameId)
                ? windowedCandidates[idx].id
                : null
            )
            .filter(Boolean)
        );
        if (hiddenIds.size > 0) {
          nextMarkets = nextMarkets.filter((m) => !hiddenIds.has(m.id));
        }
      }

      setMarkets(nextMarkets);

      let predMap = {};
      if (predictionPayload && typeof predictionPayload === 'object' && !Array.isArray(predictionPayload)) {
        predMap = predictionPayload;
      } else if (Array.isArray(predictionPayload)) {
        predictionPayload.forEach((p) => {
          if (p?.marketId) predMap[p.marketId] = p;
        });
      }

      if (nextMarkets.length) {
        const trimmed = {};
        nextMarkets.forEach((market) => {
          if (predMap[market.id]) trimmed[market.id] = predMap[market.id];
        });
        setPredictions(trimmed);
      } else {
        setPredictions({});
      }
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchMarkets();
  }, [fetchMarkets]);

  return { markets, predictions, loading, error, refetch: fetchMarkets };
}
